import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { 
  DocumentArrowDownIcon, 
  ClipboardDocumentIcon,
  ChevronDownIcon,
  CalendarIcon,
  FunnelIcon,
  GlobeAltIcon,
  BuildingOfficeIcon
} from '@heroicons/react/24/outline';

// RAG Thresholds for Exchange Benchmarking
const RAG_THRESHOLDS = {
  liquidityDepth: {
    green: 500,    // > $500M
    amber: 100     // $100M - $500M, < $100M = red
  },
  fees: {
    green: 0.1,    // < 0.1% (combined maker + taker)
    amber: 0.3     // 0.1% - 0.3%, > 0.3% = red
  },
  tier: {
    1: 'green',    // Tier 1 = green
    2: 'amber',    // Tier 2 = amber  
    3: 'red',      // Tier 3 = red
    'DEX': 'amber' // DEX = amber
  }
};

// Calculate RAG status for individual metrics
const getMetricRAGStatus = (value, metric, tier = null) => {
  switch (metric) {
    case 'liquidityDepth':
      if (value > RAG_THRESHOLDS.liquidityDepth.green) return 'green';
      if (value >= RAG_THRESHOLDS.liquidityDepth.amber) return 'amber';
      return 'red';
    
    case 'fees':
      // Lower is better for fees
      if (value < RAG_THRESHOLDS.fees.green) return 'green';
      if (value <= RAG_THRESHOLDS.fees.amber) return 'amber';
      return 'red';
    
    case 'tier':
      return RAG_THRESHOLDS.tier[tier] || 'amber';
    
    default:
      return 'amber';
  }
};

// Calculate overall RAG status
const getOverallRAGStatus = (exchange) => {
  const combinedFees = exchange.makerFee + exchange.takerFee;
  const statuses = [
    getMetricRAGStatus(exchange.liquidityDepth, 'liquidityDepth'),
    getMetricRAGStatus(combinedFees, 'fees'),
    getMetricRAGStatus(null, 'tier', exchange.tier)
  ];

  // Special handling for DEX: if it's a DEX with good liquidity and reasonable fees, should be amber
  if (exchange.tier === 'DEX') {
    const hasGoodLiquidity = exchange.liquidityDepth > RAG_THRESHOLDS.liquidityDepth.amber;
    const hasReasonableFees = combinedFees <= 0.6; // More lenient for DEX
    
    if (hasGoodLiquidity && hasReasonableFees) {
      return 'amber'; // DEX with good fundamentals = amber
    }
  }

  // Overall status: red if any red, amber if any amber, green only if all green
  if (statuses.includes('red')) return 'red';
  if (statuses.includes('amber')) return 'amber';
  return 'green';
};

// Mock data for 8 exchanges (CEX and DEX)
const exchangeData = [
  {
    id: 1,
    name: 'Binance',
    liquidityDepth: 2450.5,
    makerFee: 0.02,
    takerFee: 0.04,
    tokensListed: 900,
    region: 'Global',
    latency: 12,
    tier: 1,
    type: 'CEX'
  },
  {
    id: 2,
    name: 'Coinbase',
    liquidityDepth: 1850.3,
    makerFee: 0.00,
    takerFee: 0.05,
    tokensListed: 280,
    region: 'Americas',
    latency: 18,
    tier: 1,
    type: 'CEX'
  },
  {
    id: 3,
    name: 'Kraken',
    liquidityDepth: 680.2,
    makerFee: 0.16,
    takerFee: 0.26,
    tokensListed: 200,
    region: 'Europe',
    latency: 25,
    tier: 1,
    type: 'CEX'
  },
  {
    id: 4,
    name: 'OKX',
    liquidityDepth: 1200.8,
    makerFee: 0.08,
    takerFee: 0.10,
    tokensListed: 450,
    region: 'Asia',
    latency: 15,
    tier: 1,
    type: 'CEX'
  },
  {
    id: 5,
    name: 'KuCoin',
    liquidityDepth: 180.5,
    makerFee: 0.10,
    takerFee: 0.10,
    tokensListed: 750,
    region: 'Asia',
    latency: 35,
    tier: 2,
    type: 'CEX'
  },
  {
    id: 6,
    name: 'Bybit',
    liquidityDepth: 420.7,
    makerFee: 0.02,
    takerFee: 0.06,
    tokensListed: 320,
    region: 'Asia',
    latency: 22,
    tier: 2,
    type: 'CEX'
  },
  {
    id: 7,
    name: 'Uniswap',
    liquidityDepth: 890.4,
    makerFee: 0.30,
    takerFee: 0.30,
    tokensListed: 2100,
    region: 'Global',
    latency: 45,
    tier: 'DEX',
    type: 'DEX'
  },
  {
    id: 8,
    name: 'MEXC',
    liquidityDepth: 85.2,
    makerFee: 0.20,
    takerFee: 0.20,
    tokensListed: 1600,
    region: 'Asia',
    latency: 55,
    tier: 3,
    type: 'CEX'
  }
];

// Add calculated RAG status to each exchange
const enrichedExchangeData = exchangeData.map(exchange => ({
  ...exchange,
  ragStatus: getOverallRAGStatus(exchange)
}));

// RAG Status Component with Arrow
const RAGIndicator = ({ status }) => {
  const getStatusConfig = (status) => {
    switch (status) {
      case 'green':
        return {
          bg: 'bg-green-500/20',
          border: 'border-green-500/50',
          text: 'text-green-500',
          arrow: '↑',
          label: 'Excellent'
        };
      case 'amber':
        return {
          bg: 'bg-yellow-500/20',
          border: 'border-yellow-500/50',
          text: 'text-yellow-500',
          arrow: '→',
          label: 'Good'
        };
      case 'red':
        return {
          bg: 'bg-red-500/20',
          border: 'border-red-500/50',
          text: 'text-red-500',
          arrow: '↓',
          label: 'Needs Attention'
        };
      default:
        return {
          bg: 'bg-cream-100/20',
          border: 'border-cream-100/50',
          text: 'text-cream-100',
          arrow: '→',
          label: 'N/A'
        };
    }
  };

  const config = getStatusConfig(status);

  return (
    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${config.bg} ${config.border} ${config.text}`}>
      <span className={`text-lg mr-2 ${config.text}`}>{config.arrow}</span>
      {config.label}
    </span>
  );
};

// Helper function to get status arrow symbol
const getStatusArrow = (ragStatus) => {
  switch (ragStatus) {
    case 'green': return '↑';
    case 'amber': return '→';
    case 'red': return '↓';
    default: return '→';
  }
};

// Exchange Row Component
const ExchangeRow = ({ exchange, index }) => {
  const combinedFees = exchange.makerFee + exchange.takerFee;
  
  return (
    <motion.tr
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.05 }}
      className="border-b border-white/10 hover:bg-white/5 transition-all duration-200"
    >
      <td className="px-6 py-4 text-cream-100 font-medium text-sm">
        <div className="flex items-center space-x-2">
          <span>{exchange.name}</span>
          <span className={`text-xs px-2 py-1 rounded-full ${exchange.type === 'CEX' ? 'bg-blue-500/20 text-blue-400' : 'bg-purple-500/20 text-purple-400'}`}>
            {exchange.type}
          </span>
        </div>
      </td>
      <td className="px-6 py-4 text-center">
        <span className={`text-sm font-mono ${getMetricRAGStatus(exchange.liquidityDepth, 'liquidityDepth') === 'green' ? 'text-green-500' : getMetricRAGStatus(exchange.liquidityDepth, 'liquidityDepth') === 'amber' ? 'text-yellow-500' : 'text-red-500'}`}>
          ${exchange.liquidityDepth.toFixed(1)}M
        </span>
      </td>
      <td className="px-6 py-4 text-center">
        <span className="text-cream-200 text-sm font-mono">{exchange.makerFee.toFixed(2)}%</span>
      </td>
      <td className="px-6 py-4 text-center">
        <span className="text-cream-200 text-sm font-mono">{exchange.takerFee.toFixed(2)}%</span>
      </td>
      <td className="px-6 py-4 text-center">
        <span className="text-cream-200 text-sm font-mono">{exchange.tokensListed.toLocaleString()}</span>
      </td>
      <td className="px-6 py-4 text-center">
        <span className="text-cream-200 text-sm">{exchange.region}</span>
      </td>
      <td className="px-6 py-4 text-center">
        <span className="text-cream-200 text-sm font-mono">{exchange.latency}ms</span>
      </td>
      <td className="px-6 py-4 text-center">
        <span className={`text-sm font-medium ${getMetricRAGStatus(null, 'tier', exchange.tier) === 'green' ? 'text-green-500' : getMetricRAGStatus(null, 'tier', exchange.tier) === 'amber' ? 'text-yellow-500' : 'text-red-500'}`}>
          {exchange.tier === 'DEX' ? 'DEX' : `Tier ${exchange.tier}`}
        </span>
      </td>
      <td className="px-6 py-4 text-center">
        <RAGIndicator status={exchange.ragStatus} />
      </td>
    </motion.tr>
  );
};

// Export functionality
const exportToCSV = (data, fileName) => {
  const headers = ['Exchange Name', 'Liquidity Depth ($M)', 'Maker Fee (%)', 'Taker Fee (%)', 'Tokens Listed', 'Region', 'Latency (ms)', 'Tier', 'Status Arrows'];
  const csvContent = [
    headers.join(','),
    ...data.map(row => [
      `"${row.name}"`,
      `"${row.liquidityDepth.toFixed(1)}"`,
      `"${row.makerFee.toFixed(2)}"`,
      `"${row.takerFee.toFixed(2)}"`,
      `"${row.tokensListed}"`,
      `"${row.region}"`,
      `"${row.latency}"`,
      `"${row.tier === 'DEX' ? 'DEX' : 'Tier ' + row.tier}"`,
      `"${getStatusArrow(row.ragStatus)}"`
    ].join(','))
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', `${fileName}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const exportToPDF = async (data, fileName) => {
  const printWindow = window.open('', '', 'height=900,width=1200');
  
  const currentDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
  
  const htmlContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Valeon Exchange Benchmarking Overview</title>
      <style>
        @page {
          size: landscape;
          margin: 0.5in;
        }
        
        body { 
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; 
          margin: 0;
          padding: 20px;
          color: #333;
          line-height: 1.4;
        }
        
        .header {
          text-align: center;
          margin-bottom: 30px;
          border-bottom: 3px solid #0B0E1C;
          padding-bottom: 20px;
        }
        
        .main-title {
          font-size: 28px;
          font-weight: bold;
          color: #0B0E1C;
          margin: 0 0 8px 0;
          letter-spacing: 0.5px;
        }
        
        .subtitle {
          font-size: 16px;
          color: #666;
          margin: 0;
          font-style: italic;
        }
        
        .export-date {
          font-size: 14px;
          color: #888;
          margin-top: 8px;
        }
        
        table { 
          width: 100%; 
          border-collapse: collapse; 
          margin-top: 10px;
          font-size: 11px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }
        
        th { 
          background: linear-gradient(135deg, #0B0E1C 0%, #1a1d3a 100%);
          color: white;
          padding: 10px 6px;
          text-align: center;
          font-weight: 600;
          font-size: 10px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          border: 1px solid #333;
        }
        
        td { 
          padding: 8px 6px;
          text-align: center;
          border: 1px solid #ddd;
          background-color: #fafafa;
        }
        
        tr:nth-child(even) td {
          background-color: #f5f5f5;
        }
        
        .exchange-name {
          font-weight: 600;
          color: #0B0E1C;
          text-align: left !important;
        }
        
        .cex-badge { color: #3B82F6; font-size: 9px; }
        .dex-badge { color: #8B5CF6; font-size: 9px; }
        
        .status-green { 
          color: #10B981; 
          font-weight: bold;
          font-size: 14px;
        }
        
        .status-amber { 
          color: #F59E0B; 
          font-weight: bold;
          font-size: 14px;
        }
        
        .status-red { 
          color: #EF4444; 
          font-weight: bold;
          font-size: 14px;
        }
        
        .numeric {
          font-family: 'Courier New', monospace;
          font-weight: 500;
        }
        
        .footer {
          margin-top: 30px;
          text-align: center;
          font-size: 10px;
          color: #999;
          border-top: 1px solid #ddd;
          padding-top: 15px;
        }
      </style>
    </head>
    <body>
      <div class="header">
        <h1 class="main-title">Valeon Exchange Benchmarking Overview</h1>
        <p class="subtitle">Comprehensive CEX & DEX Performance Analysis</p>
        <div class="export-date">Generated on: ${currentDate}</div>
      </div>
      
      <table>
        <thead>
          <tr>
            <th style="width: 15%;">Exchange Name</th>
            <th style="width: 12%;">Liquidity Depth ($M)</th>
            <th style="width: 10%;">Maker Fee (%)</th>
            <th style="width: 10%;">Taker Fee (%)</th>
            <th style="width: 10%;">Tokens Listed</th>
            <th style="width: 12%;">Region</th>
            <th style="width: 10%;">Latency (ms)</th>
            <th style="width: 11%;">Tier</th>
            <th style="width: 10%;">Status</th>
          </tr>
        </thead>
        <tbody>
          ${data.map(row => `
            <tr>
              <td class="exchange-name">
                ${row.name} 
                <span class="${row.type === 'CEX' ? 'cex-badge' : 'dex-badge'}">[${row.type}]</span>
              </td>
              <td class="numeric">$${row.liquidityDepth.toFixed(1)}M</td>
              <td class="numeric">${row.makerFee.toFixed(2)}%</td>
              <td class="numeric">${row.takerFee.toFixed(2)}%</td>
              <td class="numeric">${row.tokensListed.toLocaleString()}</td>
              <td>${row.region}</td>
              <td class="numeric">${row.latency}ms</td>
              <td>${row.tier === 'DEX' ? 'DEX' : 'Tier ' + row.tier}</td>
              <td class="status-${row.ragStatus}">${getStatusArrow(row.ragStatus)}</td>
            </tr>
          `).join('')}
        </tbody>
      </table>
      
      <div class="footer">
        <p>Valeon Strategic Advisory | Exchange Benchmarking Analysis<br>
        This report contains ${data.length} exchanges analyzed across CEX and DEX platforms.</p>
      </div>
    </body>
    </html>
  `;
  
  printWindow.document.write(htmlContent);
  printWindow.document.close();
  printWindow.focus();
  
  setTimeout(() => {
    printWindow.print();
    printWindow.close();
  }, 500);
};

const ExchangeBenchmarking = () => {
  const [selectedRegion, setSelectedRegion] = useState('All');
  const [selectedType, setSelectedType] = useState('All');
  const [selectedTier, setSelectedTier] = useState('All');

  // Get unique values for filter options
  const regions = ['All', ...new Set(enrichedExchangeData.map(exchange => exchange.region))];
  const types = ['All', 'CEX', 'DEX'];
  const tiers = ['All', 'Tier 1', 'Tier 2', 'Tier 3', 'DEX'];

  // Filter data based on selected filters
  const filteredData = useMemo(() => {
    return enrichedExchangeData.filter(exchange => {
      const regionMatch = selectedRegion === 'All' || exchange.region === selectedRegion;
      const typeMatch = selectedType === 'All' || exchange.type === selectedType;
      
      let tierMatch = true;
      if (selectedTier !== 'All') {
        if (selectedTier === 'DEX') {
          tierMatch = exchange.tier === 'DEX';
        } else {
          const tierNumber = parseInt(selectedTier.split(' ')[1]);
          tierMatch = exchange.tier === tierNumber;
        }
      }
      
      return regionMatch && typeMatch && tierMatch;
    });
  }, [selectedRegion, selectedType, selectedTier]);

  return (
    <div className="space-y-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="glass-card p-8"
      >
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
          <div>
            <h2 className="text-heading-lg font-serif font-bold text-cream-100 mb-2">
              Exchange Benchmarking
            </h2>
            <p className="text-cream-200">
              Comprehensive comparison and analysis of centralized and decentralized exchange performance metrics.
            </p>
          </div>
          
          {/* Export Buttons */}
          <div className="flex space-x-3 mt-4 lg:mt-0">
            <button
              onClick={() => exportToCSV(filteredData, 'Exchange_Benchmarking_Export')}
              className="flex items-center space-x-2 px-4 py-2 bg-accent-blue/20 border border-accent-blue/30 text-accent-blue rounded-lg hover:bg-accent-blue/30 transition-all duration-200"
            >
              <DocumentArrowDownIcon className="h-4 w-4" />
              <span className="text-sm font-medium">CSV</span>
            </button>
            <button
              onClick={() => exportToPDF(filteredData, 'Exchange_Benchmarking_Export')}
              className="flex items-center space-x-2 px-4 py-2 bg-accent-gold/20 border border-accent-gold/30 text-accent-gold rounded-lg hover:bg-accent-gold/30 transition-all duration-200"
            >
              <ClipboardDocumentIcon className="h-4 w-4" />
              <span className="text-sm font-medium">PDF</span>
            </button>
          </div>
        </div>

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="flex flex-wrap gap-4 mb-8 p-4 bg-white/5 rounded-xl border border-white/10"
        >
          <div className="flex items-center space-x-2">
            <FunnelIcon className="h-5 w-5 text-cream-300" />
            <span className="text-cream-200 font-medium text-sm">Filters:</span>
          </div>
          
          {/* Region Filter */}
          <div className="relative">
            <select
              value={selectedRegion}
              onChange={(e) => setSelectedRegion(e.target.value)}
              className="appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2 pr-8 text-cream-100 text-sm focus:outline-none focus:ring-2 focus:ring-accent-blue/50 focus:border-accent-blue/50"
            >
              {regions.map(region => (
                <option key={region} value={region} className="bg-primary-800 text-cream-100">
                  {region === 'All' ? 'All Regions' : region}
                </option>
              ))}
            </select>
            <ChevronDownIcon className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-cream-300 pointer-events-none" />
          </div>

          {/* Type Filter */}
          <div className="relative">
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2 pr-8 text-cream-100 text-sm focus:outline-none focus:ring-2 focus:ring-accent-blue/50 focus:border-accent-blue/50"
            >
              {types.map(type => (
                <option key={type} value={type} className="bg-primary-800 text-cream-100">
                  {type === 'All' ? 'All Types' : type === 'CEX' ? 'Centralized' : 'Decentralized'}
                </option>
              ))}
            </select>
            <ChevronDownIcon className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-cream-300 pointer-events-none" />
          </div>

          {/* Tier Filter */}
          <div className="relative">
            <select
              value={selectedTier}
              onChange={(e) => setSelectedTier(e.target.value)}
              className="appearance-none bg-white/10 border border-white/20 rounded-lg px-4 py-2 pr-8 text-cream-100 text-sm focus:outline-none focus:ring-2 focus:ring-accent-blue/50 focus:border-accent-blue/50"
            >
              {tiers.map(tier => (
                <option key={tier} value={tier} className="bg-primary-800 text-cream-100">
                  {tier === 'All' ? 'All Tiers' : tier}
                </option>
              ))}
            </select>
            <ChevronDownIcon className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-cream-300 pointer-events-none" />
          </div>

          <div className="ml-auto flex items-center space-x-2 text-cream-300 text-sm">
            <CalendarIcon className="h-4 w-4" />
            <span>Showing {filteredData.length} exchanges</span>
          </div>
        </motion.div>

        {/* Exchange Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="overflow-x-auto"
        >
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/20">
                <th className="px-6 py-4 text-left text-cream-100 font-semibold text-sm uppercase tracking-wider">
                  Exchange Name
                </th>
                <th className="px-6 py-4 text-center text-cream-100 font-semibold text-sm uppercase tracking-wider">
                  Liquidity Depth ($M)
                </th>
                <th className="px-6 py-4 text-center text-cream-100 font-semibold text-sm uppercase tracking-wider">
                  Maker Fee (%)
                </th>
                <th className="px-6 py-4 text-center text-cream-100 font-semibold text-sm uppercase tracking-wider">
                  Taker Fee (%)
                </th>
                <th className="px-6 py-4 text-center text-cream-100 font-semibold text-sm uppercase tracking-wider">
                  Tokens Listed
                </th>
                <th className="px-6 py-4 text-center text-cream-100 font-semibold text-sm uppercase tracking-wider">
                  Region
                </th>
                <th className="px-6 py-4 text-center text-cream-100 font-semibold text-sm uppercase tracking-wider">
                  Latency (ms)
                </th>
                <th className="px-6 py-4 text-center text-cream-100 font-semibold text-sm uppercase tracking-wider">
                  Tier
                </th>
                <th className="px-6 py-4 text-center text-cream-100 font-semibold text-sm uppercase tracking-wider">
                  RAG Status
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredData.map((exchange, index) => (
                <ExchangeRow key={exchange.id} exchange={exchange} index={index} />
              ))}
            </tbody>
          </table>
        </motion.div>

        {/* Summary Stats */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.6 }}
          className="mt-12 border-t border-white/10 pt-8"
        >
          <h3 className="text-heading-md font-serif font-bold text-cream-100 mb-6 text-center">
            Exchange Performance Summary
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <motion.div 
              whileHover={{ scale: 1.02 }}
              className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-6 hover:bg-blue-500/15 transition-all duration-200"
            >
              <div className="flex items-center space-x-2 mb-3">
                <BuildingOfficeIcon className="h-5 w-5 text-blue-400" />
                <span className="text-blue-400 font-semibold text-sm uppercase tracking-wide">CEX</span>
              </div>
              <div className="text-3xl font-bold text-cream-100 mb-1">
                {filteredData.filter(e => e.type === 'CEX').length}
              </div>
              <div className="text-cream-300 text-sm">centralized exchanges</div>
            </motion.div>
            
            <motion.div 
              whileHover={{ scale: 1.02 }}
              className="bg-purple-500/10 border border-purple-500/20 rounded-xl p-6 hover:bg-purple-500/15 transition-all duration-200"
            >
              <div className="flex items-center space-x-2 mb-3">
                <GlobeAltIcon className="h-5 w-5 text-purple-400" />
                <span className="text-purple-400 font-semibold text-sm uppercase tracking-wide">DEX</span>
              </div>
              <div className="text-3xl font-bold text-cream-100 mb-1">
                {filteredData.filter(e => e.type === 'DEX').length}
              </div>
              <div className="text-cream-300 text-sm">decentralized exchanges</div>
            </motion.div>
            
            <motion.div 
              whileHover={{ scale: 1.02 }}
              className="bg-green-500/10 border border-green-500/20 rounded-xl p-6 hover:bg-green-500/15 transition-all duration-200"
            >
              <div className="flex items-center space-x-2 mb-3">
                <div className="w-4 h-4 bg-green-500 rounded-full shadow-lg"></div>
                <span className="text-green-500 font-semibold text-sm uppercase tracking-wide">Excellent</span>
              </div>
              <div className="text-3xl font-bold text-cream-100 mb-1">
                {filteredData.filter(e => e.ragStatus === 'green').length}
              </div>
              <div className="text-cream-300 text-sm">top-tier performance</div>
            </motion.div>
            
            <motion.div 
              whileHover={{ scale: 1.02 }}
              className="bg-accent-gold/10 border border-accent-gold/20 rounded-xl p-6 hover:bg-accent-gold/15 transition-all duration-200"
            >
              <div className="flex items-center space-x-2 mb-3">
                <span className="text-accent-gold text-lg">💰</span>
                <span className="text-accent-gold font-semibold text-sm uppercase tracking-wide">Avg Liquidity</span>
              </div>
              <div className="text-3xl font-bold text-cream-100 mb-1">
                ${Math.round(filteredData.reduce((acc, e) => acc + e.liquidityDepth, 0) / filteredData.length)}M
              </div>
              <div className="text-cream-300 text-sm">across filtered exchanges</div>
            </motion.div>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default ExchangeBenchmarking;